import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
  Tag,
  Progress,
  Layout,
  Radio,
  Checkbox,
  Modal
} from "antd";
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
  LockOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { selfLearnerService } from "PlatForms/SelfLMS/Services/Courses/CourseService";
import { toast } from "react-toastify";
import { AssessmentNextActionEnum } from "Common/Constants/Enums/AssessmentNextActionEnum";

const { Title, Text } = Typography;
const { Content } = Layout;

const AssessmentPage = () => {
  const navigate = useNavigate();
  const { enrollmentId, moduleProgressId } = useParams();
  console.log(".......... module progress id in state", moduleProgressId);
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timer, setTimer] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [moduleDetails, setModuleDetails] = useState(null);
  const [currentAttempt, setCurrentAttempt] = useState(1);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [moduleDetailsLoading, setModuleDetailsLoading] = useState(true);
  const [assessmentLoading, setAssessmentLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const assessmentId = location.state?.assessmentId;

  useEffect(() => {
    const loadData = async () => {
      if (assessmentId && moduleProgressId) {
        try {
          // Load module details first
          await loadModuleDetails();

          // Then load assessment data
          await loadAssessmentData();

          // Mark initial load as complete
          setInitialLoadComplete(true);
        } catch (error) {
          console.error("Error loading data:", error);
          setInitialLoadComplete(true); // Still mark as complete to exit loading state
        }
      } else {
        toast.error("Assessment information missing");
        handleBack();
        setInitialLoadComplete(true);
      }
    };

    loadData();
  }, [assessmentId, moduleProgressId]);

  const loadModuleDetails = async () => {
    try {
      setModuleDetailsLoading(true);
      console.log(" Loading module details for progress ID:", moduleProgressId);

      const details = await selfLearnerService.getModuleProgressDetails(moduleProgressId);
      setModuleDetails(details);
      console.log(".................module details......in assessment page ......", details);

      // Set current attempt number and cycle
      const attemptNumber = details.attemptNumber || 1;
      const cycleNumber = details.cycleNumber || 1;

      setCurrentAttempt(attemptNumber);
      setCurrentCycle(cycleNumber);

      console.log(" Module details:in assessmnt ", {
        attemptCycleCount: details.attemptCycleCount,
        cycleNumber: cycleNumber,
        isBlocked: details.isBlocked,
        currentAttempt: attemptNumber,
        hasPreviousResult: !!details.assessmentResult
      });

      // Check if user is blocked
      if (details.isBlocked) {
        toast.error("You have exhausted all assessment attempts. This module is now blocked.");
        return;
      }

      // Check if user has already passed the assessment
      if (details.assessmentResult && details.assessmentResult.passed) {
        toast.info("You have already passed this assessment.");
        handleBack();
        return;
      }

      // Check if this is a retry attempt
      if (details.assessmentResult && !details.assessmentResult.passed) {
        const remainingAttempts = 2 - attemptNumber;
        if (remainingAttempts > 0) {
          toast.warning(`This is your attempt ${attemptNumber} of 2 in cycle ${cycleNumber}. ${remainingAttempts} attempt(s) remaining.`);
        }
      }

      return details; // Return details for promise chaining

    } catch (error) {
      console.error("Failed to load module details:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Failed to submit assessment. Please try again.";

      toast.error(errorMessage);
      throw error; // Re-throw to handle in the useEffect
    } finally {
      setModuleDetailsLoading(false);
    }
  };

  const loadAssessmentData = async () => {
    try {
      setAssessmentLoading(true);
      console.log(" Loading assessment data for ID:", assessmentId);

      const data = await selfLearnerService.getAssessmentById(assessmentId);
      setAssessmentData(data);

      // Initialize empty answers object
      const initialAnswers = {};
      data.questions?.forEach(question => {
        initialAnswers[question.id] = [];
      });
      setUserAnswers(initialAnswers);

    } catch (error) {
      console.error(" Failed to load assessment data:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Failed to submit assessment. Please try again.";

      toast.error(errorMessage);
      handleBack();
    } finally {
      setAssessmentLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, optionId, isMultiple, checked) => {
    setUserAnswers(prev => {
      const currentAnswers = prev[questionId] || [];

      if (isMultiple) {
        if (checked) {
          return {
            ...prev,
            [questionId]: [...currentAnswers, optionId]
          };
        } else {
          return {
            ...prev,
            [questionId]: currentAnswers.filter(id => id !== optionId)
          };
        }
      } else {
        return {
          ...prev,
          [questionId]: [optionId]
        };
      }
    });
  };

  const handleSubmitClick = () => {
    if (!assessmentData?.questions) {
      toast.error("No questions available to submit");
      return;
    }

    const answeredQuestions = Object.keys(userAnswers).filter(
      questionId => userAnswers[questionId]?.length > 0
    ).length;

    const totalQuestions = assessmentData.questions.length;

    if (answeredQuestions < totalQuestions) {
      Modal.confirm({
        title: 'Incomplete Assessment',
        icon: <ExclamationCircleOutlined />,
        content: `You have answered ${answeredQuestions} out of ${totalQuestions} questions. Are you sure you want to submit?`,
        okText: 'Yes, Submit Anyway',
        cancelText: 'Continue Assessment',
        onOk: () => {
          console.log(" User confirmed incomplete submission");
          handleSubmitAssessment();
        }
      });
    } else {
      console.log(" All questions answered, submitting directly");
      handleSubmitAssessment();
    }
  };

  const handleSubmitAssessment = async () => {
    if (submitting) {
      console.log(" Submission already in progress, skipping...");
      return;
    }

    try {
      setSubmitting(true);
      console.log(" Starting assessment submission...");

      // Prepare submission data with current attempt number
      const validQuestions = Object.keys(userAnswers)
        .map(questionId => {
          const questionIdNum = parseInt(questionId);
          const questionExists = assessmentData?.questions?.some(q => q.id === questionIdNum);

          if (!questionExists) {
            console.warn(`⚠️ Question ID ${questionId} not found in assessment`);
            return null;
          }

          const uniqueOptionIds = [...new Set(userAnswers[questionId])]
            .map(id => parseInt(id))
            .filter(id => !isNaN(id));

          return {
            questionId: questionIdNum,
            selectedOptionIds: uniqueOptionIds
          };
        })
        .filter(question => question !== null && question.selectedOptionIds.length > 0);

      if (validQuestions.length === 0) {
        toast.error("Please answer at least one question before submitting");
        setSubmitting(false);
        return;
      }

      const submissionData = {
        moduleProgressId: parseInt(moduleProgressId),
        assessmentId: parseInt(assessmentId),
        questions: validQuestions,
        attemptNumber: currentAttempt,
        cycleNumber: currentCycle
      };

      console.log("Submitting assessment attempt:", currentAttempt, "cycle:", currentCycle, submissionData);

      const result = await selfLearnerService.submitAssessmentResult(submissionData);

      console.log(" Assessment submission response:", result);

      if (result.nextAction !== undefined) {
        switch (result.nextAction) {
          case AssessmentNextActionEnum.ModuleCompleted:
            toast.success("Module completed successfully!");
            break;

          case AssessmentNextActionEnum.CourseCompletedWithCertificate:
            toast.success("Course completed! Course Completion Baadge is assigened to you.");
            break;

          case AssessmentNextActionEnum.ModuleAlreadyCompleted:
            toast.info("Module was already completed.");
            break;

          case AssessmentNextActionEnum.AlreadyPassed:
            toast.info("You have already passed this assessment.");
            break;

          case AssessmentNextActionEnum.BlockedNoAttemptsLeft:
            toast.error("No attempts left. Module is blocked.");
            break;

          case AssessmentNextActionEnum.CourseAlreadyCompleted:
            toast.info("Course was already completed.");
            break;

          // For other cases (2, 3, 4), continue with existing logic
          default:
            break;
        }
      }

      // Set result and show custom modal for all cases
      setAssessmentResult(result);
      setShowResults(true);

    } catch (error) {
      console.error(" Failed to submit assessment:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Failed to submit assessment. Please try again.";

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // FIXED: Correct navigation functions
  const handleBack = () => {
    navigate(`/selfLearner-dashboard/enrolled-courses/courses/${enrollmentId}`);
  };

  const handleBackToCourseList = () => {
    navigate(`/selfLearner-dashboard/enrolled-courses`);
  };

  // Enhanced Results Modal with Correct Navigation
  const ResultsModal = () => {
    if (!showResults || !assessmentResult) return null;

    const { score = 0, passed = false, requiredScore = 75, isModuleCompleted = false, nextAction } = assessmentResult;
    const remainingAttempts = 2 - currentAttempt;
    const isFinalAttempt = currentAttempt >= 2;

    // NEW: Handle nextAction cases that should show modal
    const getModalContent = () => {
      switch (nextAction) {
        case AssessmentNextActionEnum.ModuleCompleted:
          return {
            title: 'Module Completed!',
            icon: '🎉',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'Congratulations! You have successfully completed this module.'
          };

        case AssessmentNextActionEnum.CourseCompletedWithCertificate:
          return {
            title: 'Course Completed!',
            icon: '🏆',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'Congratulations! You have successfully completed the course and earned a Badge.'
          };

        case AssessmentNextActionEnum.ModuleAlreadyCompleted:
          return {
            title: 'Already Completed',
            icon: '',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'This module was already completed.'
          };

        case AssessmentNextActionEnum.AlreadyPassed:
          return {
            title: 'Already Passed',
            icon: '',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'You have already passed this assessment.'
          };

        case AssessmentNextActionEnum.CourseAlreadyCompleted:
          return {
            title: 'Course Already Completed',
            icon: '🏆',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'This course was already completed.'
          };

        case AssessmentNextActionEnum.BlockedNoAttemptsLeft:
          return {
            title: 'Module Blocked',
            icon: '🔒',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'You have exhausted all assessment attempts. Module is now blocked.'
          };

        case AssessmentNextActionEnum.RetryAllowed:
          return {
            title: 'Assessment Failed',
            icon: '📝',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: `You have ${remainingAttempts} attempt(s) remaining in this cycle.`
          };

        case AssessmentNextActionEnum.ModuleRecycled:
          return {
            title: 'Module Reset',
            icon: '',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'Module has been reset. You can start again from the beginning.'
          };

        case AssessmentNextActionEnum.FailedNoMoreAttempts:
          return {
            title: 'Module Blocked',
            icon: '🔒',
            message: `You scored ${score}%. Required: ${requiredScore}%.`,
            description: 'You have exhausted all assessment attempts. Module is now blocked.'
          };

        default:
          // Fallback to existing logic
          return {
            title: passed ? 'Congratulations!' : 'Assessment Failed',
            icon: passed ? '🎉' : (currentAttempt >= 2 && currentCycle >= 2 ? '🔒' : '📝'),
            message: passed ? 'Assessment passed!' : `Assessment failed. Score: ${score}%`,
            description: passed ? 'You have successfully completed this assessment.' :
              `Required: ${requiredScore}%. ${remainingAttempts} attempt(s) remaining.`
          };
      }
    };

    const modalContent = getModalContent();

    const handleRetry = () => {
      setShowResults(false);
      setUserAnswers({});

      // Increment attempt number but keep the same cycle
      const newAttempt = currentAttempt + 1;
      setCurrentAttempt(newAttempt);

      // If this was the second attempt in a cycle, move to next cycle
      if (newAttempt > 2) {
        setCurrentCycle(currentCycle + 1);
        setCurrentAttempt(1);
      }

      setTimeRemaining(null);
      toast.info(`Starting attempt ${newAttempt} of 2 in cycle ${currentCycle}`);

      // Reset answers and reload assessment
      const initialAnswers = {};
      assessmentData.questions?.forEach(question => {
        initialAnswers[question.id] = [];
      });
      setUserAnswers(initialAnswers);
    };

    const handleClose = () => {
      setShowResults(false);

      // Check nextAction to determine navigation
      if (assessmentResult.nextAction !== undefined) {
        switch (assessmentResult.nextAction) {
          case AssessmentNextActionEnum.ModuleCompleted:
          case AssessmentNextActionEnum.ModuleAlreadyCompleted:
          case AssessmentNextActionEnum.AlreadyPassed:
          case AssessmentNextActionEnum.CourseAlreadyCompleted:
            // For module completion cases, navigate back to module
            handleBack();
            return;

          case AssessmentNextActionEnum.CourseCompletedWithCertificate:
            // NEW: Navigate to certificate page
            if (assessmentResult.certificationId) {
              navigate(`/certificate/${assessmentResult.certificationId}`);
              return;
            } else {
              toast.error("Certificate ID not found");
              handleBack();
              return;
            }

          case AssessmentNextActionEnum.BlockedNoAttemptsLeft:
            // For blocked cases, navigate to course list
            handleBackToCourseList();
            return;

          default:
            break;
        }
      }

      // Fallback to existing logic
      if (assessmentResult.passed || assessmentResult.nextAction === AssessmentNextActionEnum.ModuleCompleted) {
        // Passed or module completed - navigate to module with success
        handleBack();
      } else if (assessmentResult.nextAction === AssessmentNextActionEnum.FailedNoMoreAttempts ||
        assessmentResult.nextAction === AssessmentNextActionEnum.BlockedNoAttemptsLeft ||
        (currentAttempt >= 2 && currentCycle >= 2)) {
        // Failed final attempt in second cycle or blocked - navigate to course list
        toast.error("You have failed both assessment attempts. Module is now blocked.");
        handleBackToCourseList();
      } else {
        // Failed but has retries - navigate back to module
        navigate(`/selfLearner-dashboard/enrolled-courses/courses/${enrollmentId}`, {
          state: {
            assessmentFailed: true,
            score: assessmentResult.score,
            passed: assessmentResult.passed,
            requiredScore: assessmentResult.requiredScore,
            remainingAttempts: 2 - currentAttempt,
            assessmentTitle: assessmentData?.title,
            currentCycle: currentCycle,
            currentAttempt: currentAttempt
          }
        });
      }
    };

    return (
      <Modal
        open={showResults}
        onCancel={handleClose}
        footer={null}
        width={450}
        centered
        closable={false}
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {/* Header Icon */}
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {modalContent.icon}
          </div>

          {/* Title */}
          <Title level={3} style={{ marginBottom: '8px' }}>
            {modalContent.title}
          </Title>

          {/* Score Display */}
          <div style={{ marginBottom: '24px' }}>
            <Progress
              type="circle"
              percent={score}
              status={passed ? 'success' : 'exception'}
              format={percent => `${percent.toFixed(1)}%`}
              size={100}
            />
          </div>

          {/* Result Details */}
          <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '16px' }}>
              {modalContent.message}
            </Text>
            <Text type="secondary">
              {modalContent.description}
            </Text>
            <Tag color={passed ? "green" : "red"} style={{ fontSize: '14px' }}>
              {passed ? 'PASSED' : 'FAILED'}
            </Tag>

            {/* Attempt Information */}
            <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
              <Text type="secondary">
                Cycle {currentCycle}, Attempt {currentAttempt} of 2
              </Text>
              {!passed && !isFinalAttempt && (
                <div>
                  <Text type="warning">
                    {remainingAttempts} attempt(s) remaining in this cycle
                  </Text>
                </div>
              )}
            </div>

            {isModuleCompleted && (
              <Tag color="blue" style={{ fontSize: '14px' }}>
                MODULE COMPLETED
              </Tag>
            )}
          </Space>

          {/* Action Buttons */}
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* First check for CourseCompletedWithCertificate */}
            {nextAction === AssessmentNextActionEnum.CourseCompletedWithCertificate ? (
              <Button
                type="primary"
                size="large"
                onClick={handleClose}
                style={{ width: '100%' }}
              >
                View Badge
              </Button>
            ) : passed || nextAction === AssessmentNextActionEnum.ModuleCompleted ? (
              <Button
                type="primary"
                size="large"
                onClick={handleClose}
                style={{ width: '100%' }}
              >
                Back to Module
              </Button>
            ) : (nextAction === AssessmentNextActionEnum.FailedNoMoreAttempts ||
              nextAction === AssessmentNextActionEnum.BlockedNoAttemptsLeft ||
              (isFinalAttempt && currentCycle >= 2)) ? (
              <>
                <Alert
                  message="Module Blocked"
                  description="You have exhausted all assessment attempts. Please contact your administrator."
                  type="error"
                  showIcon
                  icon={<LockOutlined />}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleBackToCourseList}
                  style={{ width: '100%' }}
                >
                  Back to Course List
                </Button>
              </>
            ) : (
              <>
                {/* Only show Try Again button if currentAttempt is less than 2 */}
                {currentAttempt < 2 && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleRetry}
                    icon={<RedoOutlined />}
                    style={{ width: '100%' }}
                  >
                    Try Again (Attempt {currentAttempt + 1})
                  </Button>
                )}
                <Button
                  type="default"
                  size="large"
                  onClick={handleClose}
                  style={{ width: '100%' }}
                >
                  Review Content First
                </Button>
              </>
            )}
          </Space>
        </div>
      </Modal>
    );
  };

  // Show loading while checking module status
  if (!initialLoadComplete || moduleDetailsLoading || assessmentLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Check if user is blocked
  if (moduleDetails?.isBlocked) {
    return (
      <Alert
        message="Module Blocked"
        description="You have exhausted all assessment attempts for this module. Please contact your administrator."
        type="error"
        showIcon
        icon={<LockOutlined />}
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBackToCourseList}>
            Back to Course List
          </Button>
        }
      />
    );
  }

  if (!assessmentData) {
    return (
      <Alert
        message="Assessment Not Found"
        description="The requested assessment could not be loaded."
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Content style={{ padding: "24px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>

          {/* Header Card */}
          <Card
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: 'none',
              background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Space direction="vertical" size="small">
                  <Title level={2} style={{ margin: 0, color: 'white' }}>
                    📝 {assessmentData.title}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Final Assessment | {assessmentData.questions?.length || 0} Questions
                    {timeRemaining !== null && ` | Time Remaining: ${formatTime(timeRemaining)}`}
                    {moduleDetails?.assessmentResult && ` | Cycle ${currentCycle}, Attempt ${currentAttempt} of 2`}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Button
                  type="default"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white'
                  }}
                >
                  Back to Module
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Assessment Questions */}
          <Card
            style={{
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e8e8e8',
              borderRadius: '12px',
              background: 'white'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Alert
                message="Assessment Instructions"
                description={
                  `This is attempt ${currentAttempt} of 2 in cycle ${currentCycle}. Answer all questions carefully.`
                }
                type="info"
                showIcon
              />

              <Divider />

              {/* Questions List */}
              {assessmentData.questions?.map((question, index) => {
                const correctOptions = question.options?.filter(opt => opt.isCorrect);
                const isMultipleCorrect = correctOptions?.length > 1;

                return (
                  <div
                    key={question.id}
                    style={{
                      marginBottom: 32,
                      padding: '20px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '8px',
                      background: '#fafafa'
                    }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                      <Text strong style={{ fontSize: '16px' }}>
                        Question {index + 1}: {question.questionText}
                      </Text>

                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {isMultipleCorrect ? 'Select all that apply' : 'Select one answer'}
                      </Text>

                      <Space direction="vertical" style={{ width: "100%" }} size="small">
                        {question.options?.map((option, optIndex) => {
                          const isSelected = userAnswers[question.id]?.includes(option.id);

                          return (
                            <div
                              key={option.id}
                              style={{
                                padding: '12px 16px',
                                border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                backgroundColor: isSelected ? '#e6f7ff' : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => handleAnswerChange(
                                question.id,
                                option.id,
                                isMultipleCorrect,
                                !isSelected
                              )}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {isMultipleCorrect ? (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(e) => handleAnswerChange(
                                      question.id,
                                      option.id,
                                      true,
                                      e.target.checked
                                    )}
                                  />
                                ) : (
                                  <Radio
                                    checked={isSelected}
                                    onChange={(e) => handleAnswerChange(
                                      question.id,
                                      option.id,
                                      false,
                                      e.target.checked
                                    )}
                                  />
                                )}
                                <Text style={{ flex: 1 }}>{option.text}</Text>
                              </div>
                            </div>
                          );
                        })}
                      </Space>
                    </Space>
                  </div>
                );
              })}

              {/* Submission Section */}
              <Divider />

              <Row justify="space-between" align="middle">
                <Col>
                  <Text type="secondary">
                    {Object.keys(userAnswers).filter(q => userAnswers[q]?.length > 0).length} of {assessmentData.questions?.length} questions answered
                  </Text>
                </Col>
                <Col>
                  <Space>
                    <Button
                      type="default"
                      onClick={handleBack}
                      size="large"
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSubmitClick}
                      loading={submitting}
                      size="large"
                      icon={<CheckSquareOutlined />}
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : `Submit Attempt ${currentAttempt}`}
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>

          {/* Results Modal */}
          <ResultsModal />
        </Space>
      </Content>
    </Layout>
  );
};

export default AssessmentPage;